import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import CustomLink from "./CustomLink";

interface MarkdownPreviewProps {
  content: string;
  className?: string;
}

// Helper function to extract text from React children (including nested elements)
const extractText = (children: any): string => {
  if (typeof children === "string") {
    return children;
  }
  if (Array.isArray(children)) {
    return children.map(extractText).join("");
  }
  if (children?.props?.children) {
    return extractText(children.props.children);
  }
  return "";
};

// Helper function to extract YouTube video ID from various URL formats
const getYouTubeVideoId = (url: string): string | null => {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /^([a-zA-Z0-9_-]{11})$/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

// Helper function to check if a paragraph contains only a YouTube URL
const isYouTubeUrl = (text: string): boolean => {
  const trimmed = text.trim();
  return (
    trimmed.includes("youtube.com/watch") ||
    trimmed.includes("youtu.be/") ||
    trimmed.includes("youtube.com/embed/")
  );
};

const MarkdownPreview: React.FC<MarkdownPreviewProps> = ({
  content,
  className = "",
}) => {
  return (
    <div
      className={`markdown-body ${className}`}
      style={{
        fontFamily: '"Calibri", "Arial", sans-serif',
        fontSize: "12pt",
        // lineHeight: "1.1",
        color: "hsl(var(--foreground))",
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          // Custom link component for handling internal/external links and YouTube
          a: ({ href, children, ...props }) => {
            // Check if this is a YouTube link with "YouTube Tutorial" or similar text
            const childText = children?.toString() || "";
            if (
              href &&
              (href.includes("youtube.com") || href.includes("youtu.be"))
            ) {
              const videoId = getYouTubeVideoId(href);
              if (videoId) {
                return (
                  <div
                    style={{
                      margin: "20px 0",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <iframe
                      width="560"
                      height="315"
                      src={`https://www.youtube.com/embed/${videoId}?rel=0`}
                      title={childText || "YouTube video"}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{ maxWidth: "100%" }}
                    />
                  </div>
                );
              }
            }

            return (
              <CustomLink href={href} {...props}>
                {children}
              </CustomLink>
            );
          },
          // Custom paragraph to detect standalone YouTube URLs
          p: ({ children, ...props }) => {
            const childText = children?.toString() || "";

            // Check if paragraph contains only a YouTube URL
            if (isYouTubeUrl(childText)) {
              const videoId = getYouTubeVideoId(childText);
              if (videoId) {
                return (
                  <div
                    style={{
                      margin: "20px 0",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <iframe
                      width="560"
                      height="315"
                      src={`https://www.youtube.com/embed/${videoId}?rel=0`}
                      title="YouTube video"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{ maxWidth: "100%" }}
                    />
                  </div>
                );
              }
            }

            return (
              <p
                style={{
                  marginBottom: "10pt",
                  marginTop: "0pt",
                  textAlign: "left",
                }}
                {...props}
              >
                {children}
              </p>
            );
          },
          // Custom heading components with IDs for fragment navigation
          h1: ({ children, ...props }) => {
            const text = extractText(children);
            const id = text
              .toLowerCase()
              .replace(/\s+/g, "-")
              .replace(/[^\w-]/g, "");
            return (
              <h1
                id={id}
                style={{
                  fontSize: "20pt",
                  fontWeight: "bold",
                  marginTop: "20pt",
                  marginBottom: "6pt",
                  color: "hsl(var(--foreground))",
                }}
                {...props}
              >
                {children}
              </h1>
            );
          },
          h2: ({ children, ...props }) => {
            const text = extractText(children);
            const id = text
              .toLowerCase()
              .replace(/\s+/g, "-")
              .replace(/[^\w-]/g, "");
            return (
              <h2
                id={id}
                style={{
                  fontSize: "16pt",
                  fontWeight: "bold",
                  marginTop: "18pt",
                  marginBottom: "4pt",
                  color: "hsl(var(--foreground))",
                }}
                {...props}
              >
                {children}
              </h2>
            );
          },
          h3: ({ children, ...props }) => {
            const text = extractText(children);
            const id = text
              .toLowerCase()
              .replace(/\s+/g, "-")
              .replace(/[^\w-]/g, "");
            return (
              <h3
                id={id}
                style={{
                  fontSize: "14pt",
                  fontWeight: "bold",
                  marginTop: "14pt",
                  marginBottom: "4pt",
                  color: "hsl(var(--foreground))",
                }}
                {...props}
              >
                {children}
              </h3>
            );
          },
          h4: ({ children, ...props }) => {
            const text = extractText(children);
            const id = text
              .toLowerCase()
              .replace(/\s+/g, "-")
              .replace(/[^\w-]/g, "");
            return (
              <h4
                id={id}
                style={{
                  fontSize: "12pt",
                  fontWeight: "bold",
                  marginTop: "12pt",
                  marginBottom: "2pt",
                  color: "hsl(var(--foreground))",
                }}
                {...props}
              >
                {children}
              </h4>
            );
          },
          h5: ({ children, ...props }) => {
            const text = extractText(children);
            const id = text
              .toLowerCase()
              .replace(/\s+/g, "-")
              .replace(/[^\w-]/g, "");
            return (
              <h5
                id={id}
                style={{
                  fontSize: "11pt",
                  fontWeight: "bold",
                  marginTop: "11pt",
                  marginBottom: "2pt",
                  color: "hsl(var(--foreground))",
                }}
                {...props}
              >
                {children}
              </h5>
            );
          },
          h6: ({ children, ...props }) => {
            const text = extractText(children);
            const id = text
              .toLowerCase()
              .replace(/\s+/g, "-")
              .replace(/[^\w-]/g, "");
            return (
              <h6
                id={id}
                style={{
                  fontSize: "10pt",
                  fontWeight: "bold",
                  marginTop: "10pt",
                  marginBottom: "2pt",
                  color: "hsl(var(--foreground))",
                }}
                {...props}
              >
                {children}
              </h6>
            );
          },
          // Lists
          ul: ({ children, ...props }) => (
            <ul
              style={{
                marginLeft: "36pt",
                marginBottom: "10pt",
                marginTop: "0pt",
                listStyleType: "disc",
                paddingLeft: "20pt",
              }}
              {...props}
            >
              {children}
            </ul>
          ),
          ol: ({ children, ...props }) => (
            <ol
              style={{
                marginLeft: "36pt",
                marginBottom: "10pt",
                marginTop: "0pt",
                listStyleType: "decimal",
                paddingLeft: "20pt",
              }}
              {...props}
            >
              {children}
            </ol>
          ),
          li: ({ children, ...props }) => (
            <li
              style={{ marginBottom: "5pt", display: "list-item" }}
              {...props}
            >
              {children}
            </li>
          ),
          // Blockquote
          blockquote: ({ children, ...props }) => (
            <blockquote
              style={{
                borderLeft: "3px solid hsl(var(--border))",
                paddingLeft: "10pt",
                marginLeft: "0pt",
                fontStyle: "italic",
                color: "hsl(var(--muted-foreground))",
                marginBottom: "10pt",
              }}
              {...props}
            >
              {children}
            </blockquote>
          ),
          // Code
          code: ({ children, ...props }) => (
            <code
              style={{
                backgroundColor: "hsl(var(--muted))",
                padding: "2px 4px",
                fontFamily: '"Courier New", monospace',
                fontSize: "10pt",
                border: "1px solid hsl(var(--border))",
              }}
              {...props}
            >
              {children}
            </code>
          ),
          pre: ({ children, ...props }) => (
            <pre
              style={{
                backgroundColor: "hsl(var(--muted))",
                padding: "10pt",
                border: "1px solid hsl(var(--border))",
                overflow: "auto",
                marginBottom: "10pt",
                fontFamily: '"Courier New", monospace',
                fontSize: "10pt",
              }}
              {...props}
            >
              {children}
            </pre>
          ),
          // Horizontal rule
          hr: ({ ...props }) => (
            <hr
              style={{
                border: "none",
                borderTop: "1px solid hsl(var(--border))",
                margin: "12pt 0",
              }}
              {...props}
            />
          ),
          // Strong/Bold
          strong: ({ children, ...props }) => (
            <strong style={{ fontWeight: "bold" }} {...props}>
              {children}
            </strong>
          ),
          // Emphasis/Italic
          em: ({ children, ...props }) => (
            <em style={{ fontStyle: "italic" }} {...props}>
              {children}
            </em>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownPreview;
